import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { useLoad } from '../../../../../shared/hooks/load'
import { IconEdit } from '../../../../../shared/icon/icon'
import { useRoleOrgSSO } from '../../../../../role/hook/role-org'
import { FormNew } from '../../../../../shared/form-new/form'
import { authGet, authPut } from '../../../../../shared/sso-workspace/auth-api'
import { WorkspaceShell, WorkspaceSection } from '../../../../../shared/sso-workspace/workspace-shell'
import { connectionUse } from '../../../../../shared/sso-workspace/connection-use'
import { SaveBar } from '../../../../../shared/sso-workspace/save-bar'
import { UnsavedChangesProvider } from '../../../../../shared/sso-workspace/unsaved-changes'
import { scrollToSection } from '../../../../../shared/sso-workspace/section-nav'
import { ProviderLogo } from '../../../../../shared/sso-workspace/provider-logo'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { useAuditRecord } from '../../../../../shared/audit/history/audit-record-context'
import { WORKSPACE_SECTIONS, DirtyGuard, HeaderSave, ClearErrorWhenClean } from '../../../admin/provider/page/workspace/provider-workspace'
import { SectionIdentity } from '../../../admin/provider/page/workspace/section-identity'
import { SectionSignout } from '../../../admin/provider/page/workspace/section-signout'
import { SectionHelp } from '../../../admin/provider/page/workspace/section-help'
import { SectionValues } from '../../../admin/provider/page/workspace/section-values'
import { SectionActivity } from '../../../admin/provider/page/workspace/section-activity'
import { toProviderFormState, connectionSubtitle, invalidFieldsMessage } from '../../../admin/provider/page/workspace/fields'
import { ConnectionStatusSwitch, ConnectionDomains, ConnectionClaims } from './org-connection-sections'
import { ConnectionTesters, TestingSteps, inUse } from './org-sso-testing'
import { managedNote } from './org-sso'

// The sections an organization sees on its connection page, in the staff
// workspace's order and words (WORKSPACE_SECTIONS): on a connection it runs
// itself (auth's selfServe), every section it may act in; on one EducationXR
// manages, the two that describe it. Credentials, Provisioning (on the
// Authentication page) and anything staff-only never show here.
const SELF_SERVE = ['idp', 'routing', 'claims', 'activity', 'signout', 'testing', 'help', 'values']
const MANAGED = ['idp', 'routing']

export const orgConnectionSections = selfServe =>
  WORKSPACE_SECTIONS.filter(s => (selfServe ? SELF_SERVE : MANAGED).includes(s.id)).map(({ staff, ...s }) => s)

// What the owner may change (auth's ConnectionEdit): the PUT body is these
// fields only, whatever else the form holds.
const OWNER_FIELDS = ['providerName', 'displayName', 'signoutURL', 'endIdpSessionOnSignout', 'contactName', 'contactEmail', 'helpUrl', 'helpText']

// The fields the form shows (owner fields, and what Identity provider shows
// read only): the form's state is rebuilt only when one of them changes, so
// a Status switch or a test address saved at once never resets unsaved edits
// (review M3).
const FORM_FIELDS = [...OWNER_FIELDS, 'identifier', 'driver', 'connection', 'issure', 'tenantClaim', 'tenantValue', 'active']

// The PUT body: the owner fields that differ from what was loaded (loaded =
// the form's state), so a value the organization didn't touch -- one staff
// stored under older rules -- never blocks saving another (review L2).
// Without `loaded`, every owner field present.
export const ownerEdit = (values, loaded) => {
  const body = {}
  OWNER_FIELDS.forEach(key => {
    if (values[key] === undefined) return
    const value = values[key] === null ? '' : values[key]
    if (loaded) {
      const before = loaded[key] === null || loaded[key] === undefined ? '' : loaded[key]
      if (value === before) return
    }
    body[key] = value
  })
  return body
}

// The strip's tiles for this page: the domains it sends and whether it is
// being tested, each a shortcut to its section.
const orgTiles = connection => {
  if (!connection) return []
  const domains = (connection.domains || []).length
  const tiles = [{ key: 'domains', label: 'your domains', value: domains === 0 ? 'None bound' : String(domains), section: 'routing', tone: domains ? 'ok' : null, dot: domains ? 'ok' : null }]
  if (Array.isArray(connection.testAllowlist)) {
    const n = connection.testAllowlist.length
    tiles.push({ key: 'testing', label: 'who is sent here', value: n ? `Testing · ${n === 1 ? '1 person' : `${n} people`}` : 'Everyone', section: 'testing', tone: n ? 'warn' : null, dot: n ? 'warn' : null })
  }
  return tiles
}

// The organization's own page for one SSO connection (/org/sso/connections/:id),
// in the staff connection workspace's layout and side nav, filtered to what
// the organization may use. Every read and write is auth's org tier
// (/org-sso/connections/:id), which decides what the organization may see
// and change; this page only follows it.
export const OrgConnectionWorkspace = () => {
  const { providerID } = useParamsInt()
  const roleOrgSSO = useRoleOrgSSO()
  const [connection, setConnection] = useState(undefined)
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const [saved, setSaved] = useState(false)

  const load = async () => {
    setLoadError(null)
    try {
      setConnection(await authGet(`org-sso/connections/${providerID}`))
    } catch (err) {
      setConnection(null)
      setLoadError(err && err.status === 404 ? 'This connection was not found.' : "This connection couldn't be loaded.", err)
    }
  }

  useLoad(() => load(), [providerID])

  useAuditRecord({ type: 'providers', id: connection ? providerID : undefined, children: true })

  // A #section in the address (a domain's Testing badge links to #testing)
  // scrolls there once the connection has loaded.
  const { hash } = useLocation()
  const loaded = Boolean(connection)
  useEffect(() => { if (hash && loaded) scrollToSection(hash.slice(1)) }, [hash, loaded])

  const selfServe = Boolean(connection && connection.selfServe)
  const canEdit = selfServe && Boolean(roleOrgSSO.accessUpdate)
  const formKey = JSON.stringify(FORM_FIELDS.map(key => (connection ? connection[key] : undefined)))
  const formState = useMemo(() => toProviderFormState(connection || {}), [formKey]) // eslint-disable-line react-hooks/exhaustive-deps
  const sections = useMemo(() => orgConnectionSections(selfServe), [selfServe])

  const handleSubmit = async form => {
    setSaveError(null)
    try {
      setConnection(await authPut(`org-sso/connections/${providerID}`, ownerEdit(form, formState)))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      setSaveError((err && err.data && err.data.message) || 'Could not save.', err)
    }
  }

  const handleInvalid = errors => setSaveError(invalidFieldsMessage(errors, connection && connection.driver))

  const name = connection ? connection.providerName : ''

  if (connection === undefined) {
    return <BreadcrumbView Icon={IconEdit} text="Connection"><div className="sw-pane p-3"><p className="sw-hint">Loading…</p></div></BreadcrumbView>
  }

  if (connection === null) {
    return (
      <BreadcrumbView Icon={IconEdit} text="Connection">
        <div className="sw-pane p-3">
          <p className="text-danger" role="alert">{loadError}</p>
          <ErrorReference error={loadFailure} />
        </div>
      </BreadcrumbView>
    )
  }

  return (
    <BreadcrumbView Icon={IconEdit} text={name || 'Connection'}>
      <div className="sw-pane p-3" data-testid="org-connection-pane">
        <FormNew state={formState} enabled={canEdit} onSubmit={handleSubmit} onInvalid={handleInvalid}>
          <UnsavedChangesProvider>
            <WorkspaceShell
              title={name}
              subtitle={connectionSubtitle(connection)}
              logo={<ProviderLogo iconSVG={connection.iconSVG} iconSVGDark={connection.iconSVGDark} />}
              status={connectionUse({ active: connection.active, isPublic: connection.public, bound: connection.boundDomainCount, testers: Array.isArray(connection.testAllowlist) ? connection.testAllowlist.length : undefined })}
              headerActions={canEdit
                ? <React.Fragment>
                  <span role="status" className="small text-success align-self-center">{saved ? 'Saved' : ''}</span>
                  <HeaderSave />
                </React.Fragment>
                : null}
              tiles={orgTiles(connection)}
              sections={sections}
              mode="customer"
              onGo={scrollToSection}>

              {!selfServe &&
                <p className="sw-note small" data-testid="managed-note">{managedNote(connection)}</p>}

              <WorkspaceSection id="idp" title="Identity provider" mode="customer"
                lede={selfServe ? 'Who people sign in with. You can rename it and change its sign-in button; how it trusts your identity provider is set by EducationXR.' : 'Who people sign in with.'}>
                <SectionIdentity mode="customer" issuerLocked />
                {selfServe &&
                  <div className="mt-3"><ConnectionStatusSwitch connection={connection} enabled={canEdit} onChanged={setConnection} /></div>}
              </WorkspaceSection>

              <WorkspaceSection id="routing" title="Domains & organizations" mode="customer" lede="Who is sent to this connection.">
                <ConnectionDomains connection={connection} selfServe={selfServe} />
              </WorkspaceSection>

              {selfServe &&
                <React.Fragment>
                  <WorkspaceSection id="claims" title="Claims & mapping" mode="customer" lede="Roles your people get from what the identity provider says about them.">
                    <ConnectionClaims mappings={connection.claimMappings} />
                  </WorkspaceSection>

                  <WorkspaceSection id="activity" title="Sign-in activity" mode="customer" lede="Whether your people sign in through this connection.">
                    <SectionActivity providerID={providerID} activityPath={`org-sso/connections/${providerID}/activity`} showFindings={false} />
                  </WorkspaceSection>

                  <WorkspaceSection id="signout" title="Sign-out" mode="customer" lede="What happens when someone signs out of EducationXR.">
                    <SectionSignout issuerEndpoint={null} />
                  </WorkspaceSection>

                  <WorkspaceSection id="testing" title="Testing" mode="customer"
                    lede="While this list has addresses, only these people are sent to this connection. Everyone else keeps signing in the way they do now.">
                    <div className="d-grid gap-3">
                      <TestingSteps />
                      {!connection.active && inUse({ ...connection, active: true }) &&
                        <p className="sw-hint m-0">This connection is switched off. Switch it on under Identity provider to let your test people sign in.</p>}
                      <ConnectionTesters connection={connection} enabled={canEdit}
                        onChanged={list => setConnection(c => ({ ...c, testAllowlist: list }))} />
                    </div>
                  </WorkspaceSection>

                  <WorkspaceSection id="help" title="Contacts & help" mode="customer" lede="Who to contact about this connection, and what people see if they aren't set up yet.">
                    <SectionHelp />
                  </WorkspaceSection>

                  <WorkspaceSection id="values" title="Values for your identity provider" mode="customer" lede="Paste these into your identity provider if it asks for them. They never change.">
                    <SectionValues identifier={connection.identifier} />
                  </WorkspaceSection>
                </React.Fragment>}
            </WorkspaceShell>
            {canEdit && <SaveBar error={saveError} failure={saveFailure} onDiscard={() => setSaveError(null)} />}
          </UnsavedChangesProvider>
          <DirtyGuard />
          <ClearErrorWhenClean error={saveError} onClear={() => setSaveError(null)} />
        </FormNew>
      </div>
    </BreadcrumbView>
  )
}
